import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { WalletAcknowledgments } from '@root/entities/wallet/ui'
import { Input, Separator } from '@root/shared/ui-kit'

import { reatomExportWallet } from '../../atoms'

import { ExportWalletKeys } from './export-wallet-keys'

import type { DeviceType } from '@blade/ui-kit'
import type { WalletModel } from '@root/entities/wallet/atoms'
import type { AcknowledgmentValue } from '@root/entities/wallet/ui'

type ExportWalletProps = {
  wallet: WalletModel
  onClose?: () => void
  device: DeviceType
}

export const ExportWallet: React.FC<ExportWalletProps> = ({
  wallet,
  onClose,
  device,
}) => {
  const { t } = useTranslation()
  const model = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomExportWallet({ walletId: wallet.id }),
    [wallet.id],
  )
  const [walletName] = useAtom(wallet.name)
  const [exportState] = useAtom(model.exportWalletStateAtom)
  const exportWallet = useAction(model.exportWalletAction)
  const [value, setValue] = useState<AcknowledgmentValue>({
    first: false,
    second: false,
    third: false,
  })
  const enabled = value.first && value.second && value.third
  const elementHeight = device === 'mobile' ? 'm' : 'h36'

  const renderContent = () => {
    if (AsyncStateN.isSuccess(exportState)) {
      return (
        <KeysContainer>
          <Input
            startContent={t('wallet.wallet_name')}
            value={walletName}
            size={elementHeight}
            readOnly
          />
          <ExportWalletKeys onDone={onClose} keys={exportState.value} />
          <Button
            radius="rounded"
            color="primary"
            fullWidth
            size={elementHeight}
            onClick={onClose}
          >
            {t('common.done')}
          </Button>
        </KeysContainer>
      )
    }

    return (
      <>
        <WalletAcknowledgments value={value} onChange={setValue} />

        <Actions>
          <Button
            variant="secondary"
            size={elementHeight}
            radius="rounded"
            onClick={onClose}
          >
            {t('common.cancel')}
          </Button>
          <Button
            color="primary"
            size={elementHeight}
            radius="rounded"
            busy={AsyncStateN.isLoading(exportState)}
            disabled={!enabled}
            onClick={() => exportWallet()}
          >
            {t('common.continue')}
          </Button>
        </Actions>
      </>
    )
  }

  return (
    <Root>
      <Group>
        <Title>{t('common.attention')}</Title>
        <Separator />
        <Description>{t('wallet.export_wallet.description')}</Description>
      </Group>
      {renderContent()}
    </Root>
  )
}

const Root = styled.div`
  padding-block: 4px 0;

  display: grid;
  grid-template-rows: auto 1fr auto;
  align-content: start;
  align-items: start;

  height: 100%;
`

const Group = styled.div`
  display: grid;
  gap: 10px;
`

const Title = styled.div`
  color: var(--amber-9);

  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;

  place-content: center;
  display: grid;
`

const Description = styled.div`
  color: var(--text-9);
  text-align: center;

  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`

const KeysContainer = styled.div`
  padding-block: 16px 0;
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 20px;
  height: 100%;
`

const Actions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
`
